import { CLIENT_CATEGORIES, type ClientCategory } from "@/lib/cockpit-areas";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

type Ctx = { supabase: any; userId: string };

async function assertStaff(ctx: Ctx) {
  const { data: isAdmin } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (isAdmin) return "admin" as const;
  const { data: isEditor } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "editor",
  });
  if (isEditor) return "editor" as const;
  throw new Error("Forbidden");
}

async function assertAdmin(ctx: Ctx) {
  const { data: isAdmin } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (!isAdmin) throw new Error("Forbidden: Admin role required");
}

export type Client = Database["public"]["Tables"]["clients"]["Row"];
export type Entitlement = Database["public"]["Tables"]["entitlements"]["Row"];
export type CustomerProfile = Database["public"]["Tables"]["customer_profiles"]["Row"];
export type EntitlementType = Database["public"]["Enums"]["entitlement_type"];
export type ClientStatus = Database["public"]["Enums"]["client_status"];
export type ClientCategoryValue = Database["public"]["Enums"]["client_category"];
type JsonRecord = Database["public"]["Tables"]["entitlements"]["Row"]["metadata"];

// ============================================================
// CLIENTS
// ============================================================

export const listClients = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context);
    const { data, error } = await context.supabase
      .from("clients")
      .select("*")
      .order("organization_name", { ascending: true });
    if (error) throw new Error(error.message);
    return data as Client[];
  });

export const getClient = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    await assertStaff(context);
    const { data: row, error } = await context.supabase
      .from("clients")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row as Client | null;
  });

const clientInput = z.object({
  id: z.string().uuid().optional(),
  organization_name: z.string().trim().min(1, "Organisationsname erforderlich"),
  // Ticket G14: client_number (alte Website-Kundennummer) ist veraltet und wird
  // nicht mehr über Formulare gepflegt. Fachliche Nummer = moco_client_number.
  status: z.enum(["active", "inactive", "archived"]).default("active"),
  // Ticket K9: Kategorie steuert die Cockpit-Sicht (Pflichtfeld, Standard Agenturkunde).
  category: z.enum(CLIENT_CATEGORIES as [ClientCategory, ...ClientCategory[]]).default("agenturkunde"),
  moco_client_number: z.string().trim().optional().nullable(),
  moco_report_url: z
    .string()
    .trim()
    .optional()
    .nullable()
    .refine(
      (v) => !v || /^https:\/\/[^\s]+$/i.test(v),
      "Bitte eine vollständige https-URL angeben.",
    ),
  account_manager_id: z.string().uuid().optional().nullable(),
  // Ticket AV6: Notizfeld entfallen (bei 35 Kunden nie belegt).
  // Ticket Z1: Mandatsfelder – rein informativ, ohne Wirkung auf die Cockpit-Sicht.
  mandate_type: z
    .enum([
      "buergermeister",
      "oberbuergermeister",
      "landrat",
      "direktkandidat",
      "partei_gliederung",
      "verband",
      "kommune",
      "personalratsliste",
      "sonstiges",
    ])
    .optional()
    .nullable(),
  election_outcome: z.enum(["offen", "gewonnen", "verloren"]).default("offen"),
  election_day: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein gültiges Datum angeben.")
    .optional()
    .nullable(),
  term_start: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein gültiges Datum angeben.")
    .optional()
    .nullable(),
});

export const upsertClient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => clientInput.parse(input))
  .handler(async ({ context, data }) => {
    // Kundenakten pflegen Administratoren und Redaktionskonten; Löschen bleibt admin.
    const role = await assertStaff(context);
    // Die Moco-Kundennummer wird bewusst NICHT über dieses Formular gepflegt.
    // Verknüpfen/Lösen erfolgt ausschließlich im Moco-Abgleich (moco.functions.ts),
    // inklusive Ausblenden der Rechnungen und Audit-Log.
    const payload = {
      organization_name: data.organization_name,
      status: data.status,
      category: data.category,
      moco_report_url: data.moco_report_url ? data.moco_report_url.trim() : null,
      // Ansprechpartner: nur Administratoren dürfen ihn setzen oder ändern. Für
      // Editoren bleibt ein mitgesendeter Wert wirkungslos – beim Anlegen leer,
      // beim Ändern gar nicht im Änderungspaket.
      ...(role === "admin"
        ? { account_manager_id: data.account_manager_id || null }
        : data.id
          ? {}
          : { account_manager_id: null }),
      // Ticket Z1
      mandate_type: data.mandate_type ?? null,
      election_outcome: data.election_outcome,
      election_day: data.election_day || null,
      term_start: data.term_start || null,
    };
    if (data.id) {
      const { data: before } = await context.supabase
        .from("clients")
        .select("category, mandate_type, election_outcome, election_day, term_start")
        .eq("id", data.id)
        .maybeSingle();
      const { data: row, error } = await context.supabase
        .from("clients")
        .update(payload)
        .eq("id", data.id)
        .select()
        .single();
      if (error) throw new Error(error.message);

      // Ticket K9: Kategoriewechsel protokollieren.
      if (before && before.category !== data.category) {
        await (await import("@/lib/audit.server")).writeAuditEvent({
          action: "client_category_changed",
          tableName: "clients",
          recordId: data.id,
          oldData: { category: before.category },
          newData: { category: data.category },
          context: "admin_client_form",
          actorId: context.userId,
        });
      }

      // Ticket Z1: Mandatsfelder protokollieren (gleiches Muster wie Kategorie).
      if (before) {
        const tracked: Array<[keyof typeof payload, string]> = [
          ["mandate_type", "client_mandate_type_changed"],
          ["election_outcome", "client_election_outcome_changed"],
          ["election_day", "client_election_day_changed"],
          ["term_start", "client_term_start_changed"],
        ];
        for (const [field, action] of tracked) {
          const key = field as string;
          const oldValue =
            ((before as Record<string, string | null>)[key] ?? null) as string | null;
          const newValue =
            ((payload as Record<string, string | null>)[key] ?? null) as string | null;
          if (oldValue === newValue) continue;
          await (await import("@/lib/audit.server")).writeAuditEvent({
            action,
            tableName: "clients",
            recordId: data.id,
            oldData: { [key]: oldValue } as Record<string, string | null>,
            newData: { [key]: newValue } as Record<string, string | null>,
            context: "admin_client_form",
            actorId: context.userId,
          });
        }
      }

      return row as Client;
    }



    const { data: row, error } = await context.supabase
      .from("clients")
      .insert(payload)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row as Client;
  });

/**
 * Ticket K9: Kategorie separat setzen – erlaubt für Administratoren UND Editoren.
 * Zuweisungen und Daten bleiben unverändert; es ändert sich nur die Sichtbarkeit.
 */
export const setClientCategory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({ id: z.string().uuid(), category: z.enum(CLIENT_CATEGORIES as [ClientCategory, ...ClientCategory[]]) })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context);

    const { data: before } = await context.supabase
      .from("clients")
      .select("category")
      .eq("id", data.id)
      .maybeSingle();
    const { error } = await context.supabase
      .from("clients")
      .update({ category: data.category })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    if (before && before.category !== data.category) {
      await (await import("@/lib/audit.server")).writeAuditEvent({
        action: "client_category_changed",
        tableName: "clients",
        recordId: data.id,
        oldData: { category: before.category },
        newData: { category: data.category },
        context: "admin_client_category",
        actorId: context.userId,
      });
    }
    return { ok: true };
  });

export const setClientStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ id: z.string().uuid(), status: z.enum(["active", "inactive", "archived"]) }).parse(input),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context);
    const { error } = await context.supabase
      .from("clients")
      .update({ status: data.status })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/**
 * Ticket Z1: Wahltermin-Vorschlag aus dem Wahlkampf-Fragebogen desselben Kunden.
 * Reiner Vorschlag – die Übernahme erfolgt ausschließlich manuell im Formular.
 */
export const getClientElectionDateSuggestion = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ client_id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    await assertStaff(context);
    const { findElectionDate } = await import("@/lib/ttpa-autodraft.server");
    const value = await findElectionDate(data.client_id);
    return { election_date: value, source: "Wahlkampf-Fragebogen" as const };
  });


// ============================================================
// CUSTOMER PROFILES
// ============================================================

export const listCustomerProfiles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context);
    const { data, error } = await context.supabase
      .from("customer_profiles")
      .select("*")
      .order("last_name", { ascending: true });
    if (error) throw new Error(error.message);
    return data as CustomerProfile[];
  });

export const getCustomerProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ userId: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    await assertStaff(context);
    const { data: row, error } = await context.supabase
      .from("customer_profiles")
      .select("*")
      .eq("user_id", data.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row as CustomerProfile | null;
  });

export const updateCustomerProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        userId: z.string().uuid(),
        first_name: z.string().trim().min(1),
        last_name: z.string().trim().min(1),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    // Ticket AD6: Die eigene Zeile darf jede Person pflegen; fremde Profile
    // ausschließlich Administratorinnen (Kontoverwaltung). Der Zeilenschutz
    // setzt dieselbe Grenze durch — diese Prüfung liefert nur die sprechende
    // Meldung, statt einen rohen Zeilenschutz-Fehler zu zeigen.
    if (data.userId !== context.userId) {
      await assertAdmin(context);
    }
    const { data: row, error } = await context.supabase
      .from("customer_profiles")
      .update({ first_name: data.first_name, last_name: data.last_name })
      .eq("user_id", data.userId)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row as CustomerProfile;
  });

// ============================================================
// ENTITLEMENTS
// ============================================================

export const listEntitlements = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        userId: z.string().uuid().optional(),
        clientId: z.string().uuid().optional(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ context, data }) => {
    await assertStaff(context);
    let q = context.supabase
      .from("entitlements")
      .select("*, client:clients(id, organization_name, moco_client_number)")
      .order("granted_at", { ascending: false });
    if (data.userId) q = q.eq("user_id", data.userId);
    if (data.clientId) q = q.eq("client_id", data.clientId);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows as (Entitlement & { client: Pick<Client, "id" | "organization_name" | "moco_client_number"> | null })[];
  });

export const grantEntitlement = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        user_id: z.string().uuid(),
        type: z.enum(["agency_client_access", "course_purchase", "calendar_access"]),
        client_id: z.string().uuid().optional().nullable(),
        expires_at: z.string().datetime().optional().nullable(),
        metadata: z.any().optional().nullable(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { data: row, error } = await context.supabase
      .from("entitlements")
      .insert({
        user_id: data.user_id,
        type: data.type,
        client_id: data.type === "agency_client_access" ? data.client_id ?? null : null,
        granted_by: context.userId,
        expires_at: data.expires_at || null,
        metadata: (data.metadata ?? null) as JsonRecord,
        active: true,
      })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row as Entitlement;
  });

export const revokeEntitlement = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { error } = await context.supabase
      .from("entitlements")
      .update({ active: false })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteEntitlement = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    await assertAdmin(context);
    const { error } = await context.supabase
      .from("entitlements")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ============================================================
// COCKPIT CONTEXT (für /cockpit-Bereich)
// ============================================================

export type MyEntitlement = {
  id: string;
  type: EntitlementType;
  client_id: string | null;
  client: Pick<Client, "id" | "organization_name" | "moco_client_number"> | null;
  expires_at: string | null;
  metadata: JsonRecord;
};

export const getMyCockpitContext = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: profile } = await context.supabase
      .from("customer_profiles")
      .select("*")
      .eq("user_id", context.userId)
      .maybeSingle();

    const { data: ents, error } = await context.supabase
      .from("entitlements")
      .select("id, type, client_id, expires_at, metadata, client:clients(id, organization_name, moco_client_number)")
      .eq("user_id", context.userId)
      .eq("active", true)
      .or("expires_at.is.null,expires_at.gt." + new Date().toISOString());
    if (error) throw new Error(error.message);

    return {
      profile: profile as CustomerProfile | null,
      entitlements: (ents ?? []) as MyEntitlement[],
    };
  });
